'use client'

import { useState, useEffect, useRef } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { formatPrice } from '@/lib/currency/format'
import { Ticket as TicketIcon, Check, Clock, Award, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import PromoCodeInput from './PromoCodeInput'
import { analytics } from '@/lib/analytics'
import { fetchCertificateTemplateTypes } from '@/lib/certificates/templateTypes'

interface Ticket {
  id: string
  content_id: string
  name: string
  description: string | null
  price: number
  currency: string | null
  status: 'available' | 'sold_out' | 'discontinued'
  max_quantity: number | null
  sold_quantity: number | null
  sale_start_date: string | null
  sale_end_date: string | null
  requires_promocode: boolean
  allow_certificate?: boolean | null
  certificate_template_id?: string | null
  // Resolved client-side from a separate certificate_templates lookup.
  certificate_template_type?: 'participant' | 'speaker' | null
}

interface PromoCodeEffect {
  unlockedTicketIds?: string[]
  discountType?: 'percentage' | 'fixed'
  discountValue?: number
  appliesTo?: 'all' | string[]
}

interface TicketSelection {
  ticketId: string
  quantity: number
  price: number
  name: string
}

interface TicketSelectorProps {
  eventId?: string // Deprecated: use contentId instead
  contentId?: string // Generic content ID (event or course)
  contentType?: 'event' | 'course' // Type of content for API endpoint
  onSelectionChange: (selections: TicketSelection[], totalPrice: number, promocodeId?: string) => void
  initialPromoCode?: string // From URL parameter
}

export default function TicketSelector({
  eventId,
  contentId,
  contentType = 'event',
  onSelectionChange,
  initialPromoCode
}: TicketSelectorProps) {
  // Support both eventId (deprecated) and contentId
  const resolvedContentId = contentId || eventId || ''
  const { t, language } = useLanguage()
  const lang: 'uk' | 'en' = language === 'en' ? 'en' : 'uk'
  const supabase = createClient()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [promoEffect, setPromoEffect] = useState<PromoCodeEffect | null>(null)
  const [promocodeId, setPromocodeId] = useState<string>()
  const promoInputRef = useRef<HTMLDivElement>(null)
  // When RPC returns no tickets, check if tickets existed but their sale window expired
  const [emptyReason, setEmptyReason] = useState<'none' | 'sales_ended'>('none')
  // A failed load is NOT an empty ticket list. Rendering "no tickets available"
  // for a timed-out or rate-limited request tells the user registration is
  // closed when it is wide open – that is what a user hit on 2026-08-28 while
  // her Supabase requests were being aborted, and why she gave up and wrote in.
  const [loadFailed, setLoadFailed] = useState(false)

  const fetchTickets = async () => {
    setLoading(true)
    setLoadFailed(false)

    // Use server-side RPC for sale date filtering – database now() is
    // reliable even when the browser's system clock has a wrong timezone.
    const { data, error } = await supabase
      .rpc('get_available_tickets', { p_content_id: resolvedContentId })

    if (error || !data) {
      setLoadFailed(true)
      setTickets([])
      setLoading(false)
      return
    }

    {
      // Annotate each ticket with its bound certificate_template's role
      // so the card can show a "Certificate: Participant/Speaker" badge.
      const rows = data as Ticket[]
      const templateIds = Array.from(new Set(
        rows
          .map(t => t.certificate_template_id)
          .filter((id): id is string => !!id)
      ))
      // Resolved through the RPC: RLS hides the organizer's templates from a
      // buyer, so a direct read of certificate_templates is always empty here.
      let typeMap: Record<string, 'participant' | 'speaker'> = {}
      try {
        typeMap = await fetchCertificateTemplateTypes(supabase, templateIds)
      } catch (err) {
        console.error('Error resolving certificate template types:', err)
      }
      setTickets(rows.map(t => ({
        ...t,
        certificate_template_type: t.certificate_template_id
          ? (typeMap[t.certificate_template_id] ?? null)
          : null,
      })))

      // If RPC returned nothing, check whether tickets exist outside the sale window
      if (data.length === 0) {
        const { count } = await supabase
          .from('tickets')
          .select('*', { count: 'exact', head: true })
          .eq('content_id', resolvedContentId)
          .eq('status', 'available')

        setEmptyReason(count && count > 0 ? 'sales_ended' : 'none')
      }
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchTickets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedContentId])

  useEffect(() => {
    // Calculate and notify parent of selection changes
    const selectedTickets: TicketSelection[] = []
    let total = 0

    if (selectedTicketId) {
      const ticket = tickets.find(t => t.id === selectedTicketId)
      if (ticket) {
        let price = ticket.price

        // Apply discount if promocode is active
        if (promoEffect?.discountValue) {
          if (promoEffect.appliesTo === 'all' || promoEffect.appliesTo?.includes(selectedTicketId)) {
            if (promoEffect.discountType === 'percentage') {
              price = price * (1 - promoEffect.discountValue / 100)
            } else {
              price = Math.max(0, price - promoEffect.discountValue)
            }
          }
        }

        selectedTickets.push({
          ticketId: ticket.id,
          quantity: 1,
          price: price,
          name: ticket.name
        })

        total = price
      }
    }

    onSelectionChange(selectedTickets, total, promocodeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId, promoEffect, tickets, promocodeId])

  const handlePromoCodeValidated = (effects: PromoCodeEffect, promocodeId?: string) => {
    setPromoEffect(effects || null)
    setPromocodeId(promocodeId)
  }

  const handleTicketSelect = (ticketId: string) => {
    const ticket = tickets.find(t => t.id === ticketId)
    if (!ticket) return

    // Check if ticket is available
    const available = ticket.max_quantity !== null
      ? ticket.max_quantity - (ticket.sold_quantity || 0)
      : Infinity

    if (available === 0) return

    // Toggle selection: if already selected, deselect; otherwise select
    const newSelection = selectedTicketId === ticketId ? null : ticketId

    // Track ticket selection (only when selecting, not deselecting)
    if (newSelection !== null) {
      analytics.enrollment.ticketSelected({
        content_id: resolvedContentId,
        ticket_id: ticketId,
        ticket_name: ticket.name,
        ticket_price: ticket.price,
        quantity: 1,
        is_free: ticket.price === 0,
        has_custom_fields: false, // Will be determined by parent component
      })
    }

    setSelectedTicketId(newSelection)
  }

  // Auto-select if only one selectable ticket (not sold out)
  useEffect(() => {
    const unlocked = tickets.filter(t => !t.requires_promocode || promoEffect?.unlockedTicketIds?.includes(t.id))
    const selectable = unlocked.filter(ticket => {
      const available = ticket.max_quantity !== null
        ? ticket.max_quantity - (ticket.sold_quantity || 0)
        : Infinity
      return available > 0
    })
    if (selectable.length === 1 && selectedTicketId === null) {
      setSelectedTicketId(selectable[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, promoEffect])

  const isTicketUnlocked = (ticket: Ticket): boolean => {
    if (!ticket.requires_promocode) return true
    return promoEffect?.unlockedTicketIds?.includes(ticket.id) || false
  }

  const getDiscountedPrice = (ticket: Ticket): { original: number; discounted: number } => {
    const original = ticket.price

    if (!promoEffect?.discountValue) {
      return { original, discounted: original }
    }

    if (promoEffect.appliesTo === 'all' || promoEffect.appliesTo?.includes(ticket.id)) {
      let discounted = original

      if (promoEffect.discountType === 'percentage') {
        discounted = original * (1 - promoEffect.discountValue / 100)
      } else {
        discounted = Math.max(0, original - promoEffect.discountValue)
      }

      return { original, discounted }
    }

    return { original, discounted: original }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  // Load failure: the request never answered (timeout, rate limit, offline).
  // Never conflate this with "there are no tickets" – offer a retry instead.
  if (loadFailed) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
        <p className="font-medium text-gray-700">{t('event.registration.loadFailed')}</p>
        <p className="text-sm mt-1 text-gray-500">{t('event.registration.loadFailedHint')}</p>
        <Button variant="outline" onClick={fetchTickets} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('event.registration.retryLoad')}
        </Button>
      </div>
    )
  }

  // Empty state: no tickets returned by the RPC
  if (tickets.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {emptyReason === 'sales_ended' ? (
          <>
            <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="font-medium text-gray-700">{t('event.registration.salesEnded')}</p>
            <p className="text-sm mt-1">{t('event.registration.salesEndedHint')}</p>
          </>
        ) : (
          <>
            <TicketIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>{t('event.registration.noTicketsAvailable')}</p>
          </>
        )}
      </div>
    )
  }

  const regularTickets = tickets.filter(t => isTicketUnlocked(t))
  const allPromoOnly = regularTickets.length === 0

  return (
    <div className="space-y-6">
      {/* Promo hint when all tickets require a promo code */}
      {allPromoOnly && (
        <div className="text-center py-4">
          <TicketIcon className="h-10 w-10 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">{t('event.registration.enterPromoToAccess')}</p>
        </div>
      )}

      {/* Promocode Input – auto-expanded when all tickets are promo-only */}
      <div ref={promoInputRef}>
        <PromoCodeInput
          contentId={resolvedContentId}
          contentType={contentType}
          onPromoCodeValidated={handlePromoCodeValidated}
          initialCode={initialPromoCode}
          autoExpand={allPromoOnly}
        />
      </div>

      {/* Regular/Unlocked Tickets */}
      {regularTickets.length > 0 && (
        <div className="space-y-4">
          {regularTickets.map(ticket => {
            const isSelected = selectedTicketId === ticket.id
            const available = ticket.max_quantity !== null
              ? ticket.max_quantity - (ticket.sold_quantity || 0)
              : null
            const isSoldOut = available !== null && available === 0
            const prices = getDiscountedPrice(ticket)
            const hasDiscount = prices.original !== prices.discounted

            return (
              <div
                key={ticket.id}
                onClick={() => !isSoldOut && handleTicketSelect(ticket.id)}
                className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                  isSoldOut
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : isSelected
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-blue-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{ticket.name}</h4>
                      {ticket.requires_promocode && promoEffect?.unlockedTicketIds?.includes(ticket.id) && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                          {t('event.registration.promocode.unlocked')}
                        </span>
                      )}
                    </div>

                    {ticket.description && (
                      <p className="text-sm text-gray-600 mb-3">{ticket.description}</p>
                    )}

                    {ticket.allow_certificate && ticket.certificate_template_type && (
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          <Award className="h-3 w-3" />
                          {ticket.certificate_template_type === 'speaker'
                            ? t('event.registration.includesSpeakerCert')
                            : t('event.registration.includesParticipantCert')}
                        </span>
                      </div>
                    )}

                    <div className="flex items-baseline gap-2">
                      {hasDiscount ? (
                        <>
                          <span className="text-2xl font-bold text-green-600">
                            {formatPrice(prices.discounted, ticket.currency, lang)}
                          </span>
                          <span className="text-lg text-gray-400 line-through">
                            {formatPrice(prices.original, ticket.currency, lang)}
                          </span>
                          <span className="text-xs text-green-600 font-medium">
                            {promoEffect?.discountType === 'percentage'
                              ? `-${promoEffect.discountValue}%`
                              : `-${formatPrice(promoEffect?.discountValue ?? 0, ticket.currency, lang)}`
                            }
                          </span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-gray-900">
                          {ticket.price === 0 ? t('courseCard.free') : formatPrice(ticket.price, ticket.currency, lang)}
                        </span>
                      )}

                      {available !== null && (
                        <span className="text-xs text-gray-500 ml-2">
                          {available > 0
                            ? `${available} ${t('event.registration.ticketsAvailable')}`
                            : t('event.registration.soldOut')
                          }
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  <div className="flex items-center justify-center w-8 h-8">
                    {isSelected ? (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div className={`w-8 h-8 rounded-full border-2 ${
                        isSoldOut ? 'border-gray-300' : 'border-gray-300'
                      }`} />
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Locked Tickets (Require Promocode) - Hidden until promocode is entered */}
    </div>
  )
}
